import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './CheckboxDropdownSelect.css';

export type CDSOption = { value: string; label: string };

type Props = {
  id: string;
  placeholder?: string;
  options: CDSOption[];
  customOptions?: CDSOption[];
  value: CDSOption[];
  onChange: (next: CDSOption[]) => void;
  allowCustomValue?: boolean;
  searchPlaceholder?: string;
  maxSelected?: number;
  maxSelectedMessage?: string;
};

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();
const nameKey = (s: any) => norm(s).replace(/\s+/g, '');

const uniqueOptions = (opts: CDSOption[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const value = norm(o?.value);
    const label = norm(o?.label);
    if (!value && !label) return false;
    const key = `${value}__${label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

type OptionMeta = {
  option: CDSOption;
  valueNorm: string;
  labelNorm: string;
  nameNorm: string;
  searchNorm: string;
  looksSingleToken: boolean;
};

const toOptionMeta = (raw: CDSOption): OptionMeta | null => {
  const valueNorm = norm(raw?.value);
  const labelNorm = norm(raw?.label);
  const value = valueNorm || labelNorm;
  const label = labelNorm || valueNorm || value;
  if (!value && !label) return null;
  const nameNorm = nameKey(label || value);
  return {
    option: { value, label },
    valueNorm: value,
    labelNorm: label,
    nameNorm,
    searchNorm: `${label.toLowerCase()}\n${value.toLowerCase()}`,
    looksSingleToken: !!label && !/[\s\u3000]/.test(label),
  };
};

const toOptionMetaList = (opts: CDSOption[]): OptionMeta[] => {
  const out: OptionMeta[] = [];
  uniqueOptions(opts ?? []).forEach((raw) => {
    const meta = toOptionMeta(raw);
    if (meta) out.push(meta);
  });
  return out;
};

const stableSig = (arr: CDSOption[]): string => {
  const sorted = (arr ?? [])
    .map((o) => ({ value: norm(o.value), label: norm(o.label) }))
    .filter((o) => o.value || o.label)
    .sort((a, b) => a.value.localeCompare(b.value, 'ja') || a.label.localeCompare(b.label, 'ja'));

  try {
    return JSON.stringify(sorted);
  } catch {
    return String(Date.now());
  }
};

const useOutsideClose = (ref: React.RefObject<HTMLElement | null>, onClose: () => void) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const h = (e: MouseEvent | TouchEvent) => {
      const el = e.target as Node | null;
      if (ref.current && el && !ref.current.contains(el)) onClose();
    };

    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);

    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, [ref, onClose]);
};

const canonicalizeSelected = (raw: CDSOption[], options: CDSOption[]) => {
  const cleaned = (Array.isArray(raw) ? raw : [])
    .map((x) => ({
      value: norm((x as any)?.value),
      label: norm((x as any)?.label),
    }))
    .filter((o) => o.value || o.label);

  const list = uniqueOptions(options);

  const byValue = new Map<string, CDSOption>();
  const byLabel = new Map<string, CDSOption>();

  list.forEach((o) => {
    byValue.set(norm(o.value), o);
    if (!byLabel.has(norm(o.label))) byLabel.set(norm(o.label), o);
  });

  const out: CDSOption[] = [];
  const seenValue = new Set<string>();

  cleaned.forEach((s) => {
    const v = norm(s.value);
    const l = norm(s.label);

    const hit = byValue.get(v) || byValue.get(l) || byLabel.get(l) || byLabel.get(v);

    const next = hit
      ? { value: hit.value, label: hit.label }
      : { value: v || l, label: l || v || (v || l) };

    if (!next.value || seenValue.has(next.value)) return;
    seenValue.add(next.value);
    out.push(next);
  });

  return out;
};

const nowMs = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

/* =========================================================
 * IME debug logging (set true only when needed)
 * ======================================================= */
const IME_DEBUG = false;

const dbg = (...args: any[]) => {
  if (!IME_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};

const isEnterLike = (key: string, code: string, keyCode: number) => {
  return (
    key === 'Enter' ||
    key === 'NumpadEnter' ||
    code === 'Enter' ||
    code === 'NumpadEnter' ||
    keyCode === 13
  );
};

const keyInfoFromNative = (native: any, fallbackKey: string) => {
  const key = String(native?.key ?? fallbackKey ?? '');
  const code = String(native?.code ?? '');
  const keyCode = Number(native?.keyCode ?? native?.which ?? 0);
  const isComposing = native?.isComposing === true;
  return { key, code, keyCode, isComposing };
};

const CheckboxDropdownSelectWithAll: React.FC<Props> = ({
  id,
  placeholder,
  options,
  customOptions,
  value,
  onChange,
  allowCustomValue = false,
  searchPlaceholder = '入力で追加',
  maxSelected,
  maxSelectedMessage,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Keep UI selection until parent value catches up
  const pendingSigRef = useRef<string>('');

  const composingRef = useRef(false);

  // After compositionend, the next Enter should add this value
  const imeArmedRef = useRef(false);
  const imeArmedValueRef = useRef<string>('');
  const imeArmedAtRef = useRef<number>(0);

  // Whether IME Enter (Process/229) was seen during composition
  const sawImeEnterDuringCompositionRef = useRef(false);

  const armIme = (value: string) => {
    imeArmedRef.current = true;
    imeArmedValueRef.current = value;
    imeArmedAtRef.current = nowMs();
  };

  const clearImeArm = () => {
    imeArmedRef.current = false;
    imeArmedValueRef.current = '';
    imeArmedAtRef.current = 0;
  };

  const fallbackPlaceholder =
    placeholder && norm(placeholder) ? placeholder : '-- 選択してください --';
  const selectedLimit = useMemo(() => {
    const n = Number(maxSelected);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }, [maxSelected]);
  const trimOptionsToSelectedLimit = useCallback(
    (vals: CDSOption[]) => (selectedLimit == null ? vals : vals.slice(0, selectedLimit)),
    [selectedLimit]
  );

  const canonicalOptions = useMemo(
    () => toOptionMetaList([...(options ?? []), ...(customOptions ?? [])]).map((meta) => meta.option),
    [options, customOptions]
  );

  const [localSelected, setLocalSelected] = useState<CDSOption[]>(
    () => trimOptionsToSelectedLimit(canonicalizeSelected(value, canonicalOptions))
  );

  // props(value/options)変化時：pendingが解消するまで local を巻き戻さない
  useEffect(() => {
    const next = trimOptionsToSelectedLimit(canonicalizeSelected(value, canonicalOptions));
    const sig = stableSig(next);

    if (pendingSigRef.current) {
      if (pendingSigRef.current !== sig) {
        return;
      }

      pendingSigRef.current = '';
    }

    const curSig = stableSig(localSelected);

    if (curSig !== sig) {
      setLocalSelected(next);
    }
  }, [
    value,
    canonicalOptions,
    trimOptionsToSelectedLimit,
    localSelected,
  ]);

  useOutsideClose(rootRef, () => setOpen(false));

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const baseOptionMetaList = useMemo(() => toOptionMetaList(options ?? []), [options]);
  const baseOptions = useMemo(() => baseOptionMetaList.map((meta) => meta.option), [baseOptionMetaList]);
  const customSourceMetaList = useMemo(() => toOptionMetaList(customOptions ?? []), [customOptions]);

  const baseValueSet = useMemo(() => new Set(baseOptionMetaList.map((meta) => meta.valueNorm)), [baseOptionMetaList]);
  const baseLabelSet = useMemo(() => new Set(baseOptionMetaList.map((meta) => meta.labelNorm)), [baseOptionMetaList]);
  const baseFullNameKeySet = useMemo(
    () => new Set(baseOptionMetaList.map((meta) => meta.nameNorm).filter(Boolean)),
    [baseOptionMetaList]
  );

  const customKeySet = useMemo(() => {
    const set = new Set<string>();
    customSourceMetaList.forEach((meta) => {
      if (meta.valueNorm) set.add(`v:${meta.valueNorm}`);
      if (meta.labelNorm) set.add(`l:${meta.labelNorm}`);
    });
    return set;
  }, [customSourceMetaList]);

  const selectedSet = useMemo(() => {
    return new Set(localSelected.map((v) => v.value));
  }, [
    localSelected,
  ]);
  const isAtSelectedLimit = selectedLimit != null && selectedSet.size >= selectedLimit;
  const limitText =
    selectedLimit != null
      ? (maxSelectedMessage || `最大${selectedLimit}名まで選択できます`)
      : '';
  const trimToSelectedLimit = useCallback(
    (vals: string[]) => (selectedLimit == null ? vals : vals.slice(0, selectedLimit)),
    [selectedLimit]
  );

  const queryNormLower = useMemo(() => norm(query).toLowerCase(), [query]);

  const customOptionsForDisplay = useMemo(() => {
    const rawCustomMetaList = toOptionMetaList([...(customOptions ?? []), ...(localSelected ?? [])]);
    const out: CDSOption[] = [];
    const seen = new Set<string>();

    rawCustomMetaList.forEach((meta) => {
      const inBase =
        (meta.valueNorm && baseValueSet.has(meta.valueNorm)) ||
        (meta.labelNorm && baseLabelSet.has(meta.labelNorm)) ||
        (meta.nameNorm && baseFullNameKeySet.has(meta.nameNorm));
      if (inBase) return;

      const dedupeKey = meta.nameNorm || `${meta.valueNorm}__${meta.labelNorm}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      out.push(meta.option);
    });

    return out;
  }, [customOptions, localSelected, baseValueSet, baseLabelSet, baseFullNameKeySet]);

  const customDisplayMetaList = useMemo(() => toOptionMetaList(customOptionsForDisplay), [customOptionsForDisplay]);

  const allOptionMetaList = useMemo(() => {
    return toOptionMetaList([...(baseOptions ?? []), ...(customOptionsForDisplay ?? [])]).map((meta) => {
      const fromCustomList =
        customKeySet.size > 0 &&
        (customKeySet.has(`v:${meta.valueNorm}`) || customKeySet.has(`l:${meta.labelNorm}`));
      const matchesBaseLabel = baseLabelSet.has(meta.labelNorm);
      const matchesBaseFullKey = meta.nameNorm ? baseFullNameKeySet.has(meta.nameNorm) : false;
      const isCustom = fromCustomList || meta.looksSingleToken || (!matchesBaseLabel && !matchesBaseFullKey);
      return { ...meta, isCustom };
    });
  }, [baseOptions, customOptionsForDisplay, customKeySet, baseLabelSet, baseFullNameKeySet]);

  const allOptions = useMemo(() => allOptionMetaList.map((meta) => meta.option), [allOptionMetaList]);

  const allOptionLookup = useMemo(() => {
    const map = new Map<string, CDSOption>();
    allOptionMetaList.forEach((meta) => {
      if (meta.valueNorm && !map.has(`v:${meta.valueNorm}`)) map.set(`v:${meta.valueNorm}`, meta.option);
      if (meta.labelNorm && !map.has(`l:${meta.labelNorm}`)) map.set(`l:${meta.labelNorm}`, meta.option);
    });
    return map;
  }, [allOptionMetaList]);

  const optionsMap = useMemo(() => {
    return new Map(allOptions.map((o) => [o.value, o.label]));
  }, [allOptions]);

  const visibleBaseMetaList = useMemo(() => {
    if (!queryNormLower) return baseOptionMetaList;
    return baseOptionMetaList.filter((meta) => meta.searchNorm.includes(queryNormLower));
  }, [baseOptionMetaList, queryNormLower]);

  const visibleCustomMetaList = useMemo(() => {
    if (!queryNormLower) return customDisplayMetaList;
    return customDisplayMetaList.filter((meta) => meta.searchNorm.includes(queryNormLower));
  }, [customDisplayMetaList, queryNormLower]);

  const visibleBaseOptions = useMemo(() => visibleBaseMetaList.map((meta) => meta.option), [visibleBaseMetaList]);
  const visibleCustomOptions = useMemo(
    () => visibleCustomMetaList.map((meta) => meta.option),
    [visibleCustomMetaList]
  );

  const visibleOptions = useMemo(() => {
    return [
      ...visibleBaseOptions,
      ...visibleCustomOptions,
    ];
  }, [
    visibleBaseOptions,
    visibleCustomOptions,
  ]);

  const visibleOptionCustomSet = useMemo(() => {
    const set = new Set<string>();
    allOptionMetaList.forEach((meta) => {
      if (!meta.isCustom) return;
      set.add(meta.option.value);
    });
    return set;
  }, [allOptionMetaList]);

  const customCandidate = useMemo(() => {
    if (!allowCustomValue) return null;
    const raw = norm(query);
    if (!raw) return null;
    const exists = allOptionLookup.has(`l:${raw}`) || allOptionLookup.has(`v:${raw}`);
    if (exists) return null;
    return { value: raw, label: raw };
  }, [allowCustomValue, allOptionLookup, query]);

  // "Select all" targets only visible base options (filtered list)
  const visibleBaseValueSet = useMemo(() => new Set(visibleBaseOptions.map((o) => o.value)), [visibleBaseOptions]);

  const allVisibleSelected =
    visibleBaseOptions.length > 0 && visibleBaseOptions.every((o) => selectedSet.has(o.value));
  const visibleBaseSelectedCount = visibleBaseOptions.reduce(
    (count, o) => count + (selectedSet.has(o.value) ? 1 : 0),
    0
  );
  const maxLimitedVisibleSelected =
    selectedLimit != null &&
    visibleBaseOptions.length > selectedLimit &&
    selectedSet.size >= selectedLimit &&
    visibleBaseSelectedCount >= selectedLimit;
  const selectAllChecked = allVisibleSelected || maxLimitedVisibleSelected;
  const shouldClearVisibleSelection =
    selectAllChecked ||
    (selectedLimit != null &&
      selectedSet.size >= selectedLimit &&
      visibleBaseSelectedCount > 0);
  const someVisibleSelected =
    !selectAllChecked && visibleBaseSelectedCount > 0;

  const commit = useCallback(
    (vals: string[]) => {
      const next = trimToSelectedLimit(vals).map((v) => ({ value: v, label: optionsMap.get(v) ?? v }));
      setLocalSelected(next);

      // Preserve local UI even if parent update is delayed
      pendingSigRef.current = stableSig(next);

      onChange(next);
    },
    [onChange, optionsMap, trimToSelectedLimit]
  );

  const toggleOne = useCallback(
    (v: string) => {
      const s = new Set(selectedSet);
      const wasSelected = s.has(v);
      if (!wasSelected && isAtSelectedLimit) {
        return;
      }
      wasSelected ? s.delete(v) : s.add(v);
      commit(Array.from(s));
      if (allowCustomValue && !baseValueSet.has(norm(v))) {
        setQuery('');
      }
    },
    [commit, selectedSet, isAtSelectedLimit, allowCustomValue, baseValueSet]
  );

  const toggleAllVisible = useCallback(() => {
    if (visibleBaseOptions.length === 0) {
      if (allowCustomValue && norm(query)) setQuery('');
      return;
    }

    if (shouldClearVisibleSelection) {
      // Deselect all includes custom additions as well
      commit([]);
      if (allowCustomValue && norm(query)) setQuery('');
      return;
    }

    // Add all visible base options (union with current selection)
    const union = new Set<string>(Array.from(selectedSet));
    visibleBaseValueSet.forEach((v) => {
      if (selectedLimit != null && union.size >= selectedLimit) return;
      union.add(v);
    });
    commit(Array.from(union));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldClearVisibleSelection,
    commit,
    selectedSet,
    selectedLimit,
    visibleBaseOptions.length,
    visibleBaseValueSet,
    baseValueSet,
    allowCustomValue,
    query,
  ]);

  const hasExtraSelected = useMemo(() => {
    return (localSelected ?? []).some((s) => !baseValueSet.has(norm(s.value)));
  }, [localSelected, baseValueSet]);

  const summary = useMemo(() => {
    if (selectedSet.size === 0) return fallbackPlaceholder;

    const labelsFromOptions = baseOptions
      .filter((o) => selectedSet.has(o.value))
      .map((o) => o.label);
    const labelsFromCustom = (localSelected ?? [])
      .filter((s) => !baseValueSet.has(norm(s.value)))
      .map((s) => s.label || s.value)
      .filter((lb) => norm(lb) !== '');
    const labels = [...labelsFromOptions, ...labelsFromCustom].filter((lb) => norm(lb) !== '');

    if (labels.length === 0) return fallbackPlaceholder;
    if (labels.length === baseOptions.length && baseOptions.length > 0 && !hasExtraSelected) return 'すべて';
    if (labels.length === 1) return labels[0];

    return `${labels[0]} 他${labels.length - 1}`;
  }, [selectedSet, fallbackPlaceholder, baseOptions, localSelected, baseValueSet, hasExtraSelected]);

  const addFromQuery = useCallback(
    (rawOverride?: string) => {
      if (!allowCustomValue) return;
      const raw = norm(rawOverride ?? query);
      if (!raw) return;

      const hit = allOptionLookup.get(`l:${raw}`) ?? allOptionLookup.get(`v:${raw}`);
      const nextValue = hit ? hit.value : raw;

      if (selectedSet.has(nextValue)) {
        setQuery('');
        return;
      }
      if (isAtSelectedLimit) return;

      const next = new Set(selectedSet);
      next.add(nextValue);
      commit(Array.from(next));
      setQuery('');
    },
    [allowCustomValue, allOptionLookup, commit, query, selectedSet, isAtSelectedLimit]
  );

  /* =========================================================
   * Shared Enter handler (menu capture also handles missed input Enter)
   * ======================================================= */
  const tryCommitByEnter = useCallback(
    (e: React.KeyboardEvent<any>, source: 'input' | 'menu-capture') => {
      if (!allowCustomValue) return;

      const native = (e as any).nativeEvent ?? {};
      const info = keyInfoFromNative(native, (e as any).key);
      const enter = isEnterLike(info.key, info.code, info.keyCode);
      if (!enter) return;

      const inputValue = norm(inputRef.current?.value ?? '');
      const qValue = norm(query);
      const text = inputValue || qValue;

      if (IME_DEBUG) {
        dbg('[IME]', {
          source,
          type: 'keydown',
          key: info.key,
          code: info.code,
          keyCode: info.keyCode,
          nativeIsComposing: info.isComposing,
          composingRef: composingRef.current,
          imeArmed: imeArmedRef.current,
          imeArmedValue: imeArmedValueRef.current,
          imeArmedAt: imeArmedAtRef.current,
          sawImeEnterDuringComposition: sawImeEnterDuringCompositionRef.current,
          text,
          time: nowMs(),
        });
      }

      if (!text) return;

      const dt = imeArmedAtRef.current ? nowMs() - imeArmedAtRef.current : null;
      const armedMatches = imeArmedRef.current && norm(text) === norm(imeArmedValueRef.current);

      const allowOverrideComposing =
        armedMatches && dt != null && dt < 5000; // allow slightly delayed Enter

      const composingNow = info.isComposing || composingRef.current;

      if (composingNow && !allowOverrideComposing) {
        if (info.code === 'Enter' || info.code === 'NumpadEnter' || info.keyCode === 229) {
          sawImeEnterDuringCompositionRef.current = true;
        }
        if (IME_DEBUG) dbg('[IME] skip: composing enter');
        return;
      }

      e.preventDefault();

      if (imeArmedRef.current) clearImeArm();
      sawImeEnterDuringCompositionRef.current = false;

      if (IME_DEBUG) dbg('[IME] commit: addFromQuery()', text);
      addFromQuery(text);
    },
    [allowCustomValue, addFromQuery, query]
  );

  return (
    <div ref={rootRef} className="cds" data-open={open ? 'true' : 'false'}>
      <button type="button" className="cds__control" onClick={() => setOpen((v) => !v)}>
        <span className={`cds__placeholder ${selectedSet.size > 0 ? 'is-filled' : ''}`}>
          {summary}
        </span>
        <span className="cds__arrow" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="cds__menu"
          role="listbox"
          aria-multiselectable="true"
          onKeyDownCapture={(e) => {
            // When input is focused, let input key handler process it
            const ae = typeof document !== 'undefined' ? document.activeElement : null;
            if (ae === inputRef.current) return;

            // Capture only while IME-arm is active
            if (!imeArmedRef.current) return;

            tryCommitByEnter(e as any, 'menu-capture');
          }}
        >
          <div className="cds__search">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                const next = e.target.value;
                setQuery(next);

                // During composition, let IME own input events
                if (composingRef.current) return;

                // Clear armed value when query changed to avoid stale commit
                if (imeArmedRef.current && norm(next) !== norm(imeArmedValueRef.current)) {
                  clearImeArm();
                }
              }}
              placeholder={searchPlaceholder}
              className="cds__searchInput"
              onKeyDown={(e) => {
                tryCommitByEnter(e, 'input');
              }}
              onCompositionStart={(e) => {
                composingRef.current = true;

                if (IME_DEBUG) {
                  const ne: any = (e as any).nativeEvent ?? {};
                  dbg('[IME]', {
                    type: 'compositionstart',
                    data: String(ne.data ?? (e as any).data ?? ''),
                    isComposing: ne.isComposing === true,
                    inputValue: (e.currentTarget as HTMLInputElement)?.value ?? '',
                    time: nowMs(),
                  });
                }
              }}
              onCompositionEnd={(e) => {
                composingRef.current = false;

                const v = (e.currentTarget as HTMLInputElement).value;

                // Save composed value so next Enter always commits it
                armIme(v);
                setTimeout(() => {
                  try {
                    inputRef.current?.focus();
                  } catch {
                    /* no-op */
                  }
                }, 0);

                // onChange usually runs after compositionend and refreshes query
              }}
            />
          </div>

          <div className="cds__legend">{'\u4e0b\u7dda\u3042\u308a: \u5165\u529b\u3067\u8ffd\u52a0\u3057\u305f\u9023\u540d\u8005 / \u4e0b\u7dda\u306a\u3057: \u65e2\u5b58\u5019\u88dc'}</div>

          {/* Put "All" row inside scroll for left alignment with options */}
          <div className="cds__scroll">
            {(visibleBaseOptions.length > 0 || (allowCustomValue && norm(query))) && (
              <label className="cds__option cds__option--all">
                <input
                  type="checkbox"
                  className="cds__checkbox"
                  checked={selectAllChecked}
                  ref={(el) => {
                    if (!el) return;
                    el.indeterminate = someVisibleSelected;
                  }}
                  onChange={toggleAllVisible}
                />
                <span>すべて</span>
              </label>
            )}

            {customCandidate && (
              <label
                className={`cds__option cds__option--custom${isAtSelectedLimit ? ' is-disabled' : ''}`}
                aria-selected={selectedSet.has(customCandidate.value)}
                aria-disabled={isAtSelectedLimit}
                title={isAtSelectedLimit ? limitText : undefined}
              >
                <input
                  type="checkbox"
                  className="cds__checkbox"
                  checked={selectedSet.has(customCandidate.value)}
                  disabled={isAtSelectedLimit}
                  onChange={() => toggleOne(customCandidate.value)}
                />
                <span className="cds__optionLabel cds__optionLabel--custom">{customCandidate.label}</span>
                <span className="cds__optionNote">{'\u5165\u529b'}</span>
              </label>
            )}

            {visibleOptions.map((o) => {
              const checked = selectedSet.has(o.value);
              const isCustom = visibleOptionCustomSet.has(o.value);
              const disabled = !checked && isAtSelectedLimit;
              return (
                <label
                  key={`${id}__${o.value}`}
                  className={`cds__option${isCustom ? ' cds__option--custom' : ''}${disabled ? ' is-disabled' : ''}`}
                  aria-selected={checked}
                  aria-disabled={disabled}
                  title={disabled ? limitText : undefined}
                >
                  <input
                    type="checkbox"
                    className="cds__checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleOne(o.value)}
                  />
                  <span className={`cds__optionLabel${isCustom ? ' cds__optionLabel--custom' : ''}`}>{o.label}</span>
                  {isCustom && <span className="cds__optionNote">{'\u5165\u529b'}</span>}
                </label>
              );
            })}

            {visibleOptions.length === 0 && !customCandidate && (
              <div className="cds__empty">該当する候補がありません</div>
            )}
            {isAtSelectedLimit && limitText && (
              <div className="cds__limit">{limitText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckboxDropdownSelectWithAll;

